/**
 * Trailing "Load more" tile that sits at the end of a home carousel.
 * Same physical footprint as `HomePosterCard` so the flex layout
 * stays clean (no sizing drift when the tile appears or disappears).
 *
 * Used by `HomeView` rows whose underlying data source has more
 * items than the current `visibleCount`. Click bumps `visibleCount`
 * in the parent, which re-slices the items and reveals the next
 * batch (default page size = 20).
 */

interface Props {
  onClick: () => void
  /** Optional label override. Defaults to "Load more". */
  label?: string
  /** Optional count hint shown under the label, e.g. "+20 more". */
  hint?: string
}

export function LoadMoreCard({ onClick, label = 'Load more', hint }: Props) {
  return (
    <div
      className="shrink-0 snap-start"
      style={{ width: 'clamp(120px, 16vw, 180px)' }}
    >
      <button
        type="button"
        onClick={onClick}
        className="group flex w-full flex-col text-left focus:outline-none"
        aria-label={hint ? `${label}, ${hint}` : label}
      >
        <div className="relative flex aspect-[2/3] w-full items-center justify-center overflow-hidden rounded-lg border border-dashed border-border-primary bg-gradient-to-br from-bg-secondary to-bg-tertiary shadow-cinematic transition-all duration-300 ease-cinematic group-hover:scale-[1.03] group-hover:border-accent-primary/60 group-focus-visible:ring-2 group-focus-visible:ring-accent-primary/60">
          <div className="flex flex-col items-center gap-2 px-2 text-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-full border border-accent-primary/40 bg-accent-primary/10 text-accent-primary transition-colors group-hover:border-accent-primary group-hover:bg-accent-primary/20">
              <PlusIcon />
            </div>
            <span className="text-xs font-semibold text-text-primary sm:text-sm">
              {label}
            </span>
            {hint && (
              <span className="text-[10px] text-text-tertiary sm:text-[11px]">
                {hint}
              </span>
            )}
          </div>
        </div>
      </button>
    </div>
  )
}

function PlusIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  )
}
